import * as XLSX from 'xlsx';

/* ── Column definitions (what the sample file shows) ─────────────── */
export const CONFIG = {
  sales: {
    title: 'Sales Bills',
    sheetName: 'Sales Match',
    fileName: 'sales-match-sample.xlsx',
    columns: [
      { name: 'Bill Number',   required: true,  note: 'Your tax invoice number, e.g. AP/26-27/0001. Case, spaces and / - are ignored when matching.' },
      { name: 'Bill Date',     required: true,  note: 'Format DD-MM-YYYY (e.g. 05-10-2026). Real Excel dates also work.' },
      { name: 'Customer Name', required: false, note: 'Required if GST No is not given. Company name or customer name. "Pvt Ltd" / "Private Limited" differences are ignored.' },
      { name: 'GST No',        required: false, note: 'Required if Customer Name is not given. Customer GSTIN. Used instead of the name when both sides have it.' },
      { name: 'Grand Total',   required: true,  note: 'Invoice total including GST. Must match EXACTLY to the paisa. No ₹ needed.' },
    ],
    samples: [
      ['AP/26-27/0001', '05-10-2026', 'Sharma Traders Pvt Ltd', '10ABCDE1234F1Z5', 11800.0],
      ['AP/26-27/0002', '07-10-2026', 'Rao Enterprises',        '',                 5250.5],
    ],
    tips: [
      'Tally export: Vch No. / Particulars / GSTIN/UIN / Invoice Value columns are recognised automatically.',
      'Cancelled bills are matched too and reported as "Cancelled".',
    ],
  },
  purchases: {
    title: 'Purchases',
    sheetName: 'Purchase Match',
    fileName: 'purchase-match-sample.xlsx',
    columns: [
      { name: 'Vendor Invoice Number', required: true,  note: 'The invoice number printed on the vendor\'s bill (not your internal purchase no.).' },
      { name: 'Invoice Date',          required: true,  note: 'Format DD-MM-YYYY (e.g. 01-10-2026). Real Excel dates also work.' },
      { name: 'Vendor Name',           required: false, note: 'Required if GSTIN is not given. Must be the vendor as saved in your Vendors list.' },
      { name: 'GSTIN',                 required: false, note: 'Required if Vendor Name is not given. Vendor GSTIN — the most reliable way to identify the vendor.' },
      { name: 'Amount',                required: true,  note: 'Invoice total including GST. Must match EXACTLY to the paisa. No ₹ needed.' },
    ],
    samples: [
      ['INV/2026/0455', '01-10-2026', 'Sharma Paper Mills', '10ABCDE1234F1Z5', 59000.0],
      ['R-9981',        '03-10-2026', 'Rao Inks',           '',                 2360.0],
    ],
    tips: [
      'GST portal (GSTR-2B) export: Invoice number / Invoice Date / GSTIN of supplier / Trade name / Invoice Value columns are recognised automatically.',
      'The same invoice number can exist under two vendors, so the vendor is part of the match.',
      'Draft purchases are compared but can only be verified after they are confirmed.',
    ],
  },
};

/* ── Download helper (works in every browser / webview) ──────────── */
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
};

const saveWorkbook = (wb, filename) => {
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  downloadBlob(
    new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename,
  );
};

/* ── 1. Sample template ──────────────────────────────────────────── */
export const downloadTemplate = (type) => {
  const c = CONFIG[type];
  const wb = XLSX.utils.book_new();

  const data = [c.columns.map((x) => x.name), ...c.samples];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = c.columns.map((x) => ({ wch: Math.max(x.name.length + 4, 22) }));
  // Keep the date column as text so DD-MM-YYYY is never re-read as MM-DD
  const dateCol = 1;
  for (let r = 1; r <= c.samples.length; r += 1) {
    const cell = ws[XLSX.utils.encode_cell({ r, c: dateCol })];
    if (cell) { cell.t = 's'; cell.z = '@'; }
  }
  XLSX.utils.book_append_sheet(wb, ws, c.sheetName);

  const info = [
    ['Column', 'Required?', 'What to put'],
    ...c.columns.map((x) => [x.name, x.required ? 'Yes' : 'Yes (one of Name / GST)', x.note]),
    [],
    ['Notes'],
    ['Keep the header names in row 1 of the first sheet. Extra columns are ignored.'],
    ['Delete the two sample rows before uploading your own data.'],
    ['Maximum 5,000 rows per upload.'],
    ['Amounts are matched exactly — there is no tolerance.'],
    ...c.tips.map((t) => [t]),
  ];
  // Optional rows should say "One of" rather than "Yes"
  info.forEach((row, i) => {
    if (i > 0 && i <= c.columns.length && !c.columns[i - 1].required) row[1] = 'One of Name / GST';
  });
  const wsi = XLSX.utils.aoa_to_sheet(info);
  wsi['!cols'] = [{ wch: 26 }, { wch: 20 }, { wch: 110 }];
  XLSX.utils.book_append_sheet(wb, wsi, 'Instructions');

  saveWorkbook(wb, c.fileName);
};

/* ── 2. Read the uploaded file ───────────────────────────────────── */
// Tally / GST-portal exports often have title rows above the real header. Find the header row
// by looking for the row with the most "column-like" words.
const HEADER_WORDS = [
  'invoice', 'bill', 'date', 'amount', 'total', 'value', 'gst', 'party', 'particular',
  'vendor', 'supplier', 'customer', 'name', 'voucher', 'vch', 'number', 'no',
];
const headerScore = (cells) =>
  cells.reduce((n, c) => {
    const t = String(c ?? '').toLowerCase().trim();
    if (!t || t.length > 40) return n;
    return n + (HEADER_WORDS.some((w) => t.includes(w)) ? 1 : 0);
  }, 0);

export const parseExcelFile = async (file) => {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellDates: false });
  if (!wb.SheetNames.length) throw new Error('The file has no sheets.');

  // GSTR-2B has many sheets — the invoices live in "B2B"
  const sheetName = wb.SheetNames.find((n) => n.trim().toLowerCase() === 'b2b') || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '', blankrows: true });
  if (!grid.length) throw new Error('The first sheet is empty.');

  let headerIdx = 0;
  let best = headerScore(grid[0]);
  for (let i = 1; i < Math.min(grid.length, 25); i += 1) {
    const s = headerScore(grid[i]);
    if (s > best) { best = s; headerIdx = i; }
  }

  const headers = grid[headerIdx].map((h) => String(h ?? '').trim());
  const rows = [];
  for (let i = headerIdx + 1; i < grid.length; i += 1) {
    const obj = {};
    headers.forEach((h, c) => { if (h) obj[h] = grid[i][c] ?? ''; });
    rows.push(obj);
  }
  // Backend numbers data rows from 2 (header = row 1). Add this offset to get the real Excel row.
  const startRow = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']).s.r : 0;
  return { rows, headers, sheetName, headerOffset: startRow + headerIdx };
};

/* ── 3. Export the result report ─────────────────────────────────── */
const STATUS_TEXT = {
  matched: 'Matched', mismatch: 'Mismatch', not_found: 'Not in system',
  duplicate: 'Duplicate in Excel', cancelled: 'Cancelled', invalid: 'Invalid row',
};

export const exportReport = (type, data, fileLabel = '') => {
  const wb = XLSX.utils.book_new();

  const res = data.results.map((r) => ({
    'Excel Row': r.rowNo,
    Status: STATUS_TEXT[r.status] || r.status,
    'Excel Number': r.excel.number,
    'Excel Date': r.excel.date,
    'Excel Party': r.excel.party,
    'Excel GST': r.excel.gst,
    'Excel Amount': r.excel.amount ?? '',
    'System Number': r.system?.purchaseNumber ? `${r.system.number} (${r.system.purchaseNumber})` : (r.system?.number ?? ''),
    'System Date': r.system?.date ?? '',
    'System Party': r.system?.party ?? '',
    'System GST': r.system?.gst ?? '',
    'System Amount': r.system?.amount ?? '',
    'Verified': r.system ? (r.system.isVerified ? 'Yes' : 'No') : '',
    Differences: r.diffs.map((d) => `${d.field}: Excel "${d.excel}" vs System "${d.system}"`).join(' | '),
    Notes: r.notes.join(' | '),
  }));
  const ws1 = XLSX.utils.json_to_sheet(res.length ? res : [{ Info: 'No rows' }]);
  ws1['!cols'] = [8, 18, 22, 12, 28, 18, 14, 26, 12, 28, 18, 14, 9, 50, 50].map((w) => ({ wch: w }));
  XLSX.utils.book_append_sheet(wb, ws1, 'Results');

  const missing = (data.missingFromExcel || []).map((m) => ({
    Number: m.system.number,
    Date: m.system.date,
    Party: m.system.party,
    GST: m.system.gst,
    Amount: m.system.amount,
    Status: m.system.status,
    Verified: m.system.isVerified ? 'Yes' : 'No',
    ...(m.system.purchaseNumber ? { 'Purchase No': m.system.purchaseNumber } : {}),
  }));
  const ws2 = XLSX.utils.json_to_sheet(missing.length ? missing : [{ Info: 'None (or no date range was chosen)' }]);
  ws2['!cols'] = [22, 12, 30, 18, 14, 12, 9, 16].map((w) => ({ wch: w }));
  XLSX.utils.book_append_sheet(wb, ws2, 'In system, not in Excel');

  const s = data.summary;
  const sum = [
    ['Report', `${CONFIG[type].title} — Excel match`],
    ['Source file', fileLabel],
    ['Generated', new Date().toLocaleString('en-IN')],
    [],
    ['Rows in Excel', s.total],
    ['Matched', s.matched],
    ['Mismatch', s.mismatch],
    ['Not in system', s.not_found],
    ['Duplicate in Excel', s.duplicate],
    ['Cancelled in system', s.cancelled],
    ['Invalid rows', s.invalid],
    ['In system, not in Excel', s.missingFromExcel ?? 0],
  ];
  const ws3 = XLSX.utils.aoa_to_sheet(sum);
  ws3['!cols'] = [{ wch: 26 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, ws3, 'Summary');

  const stamp = new Date().toISOString().slice(0, 10);
  saveWorkbook(wb, `${type}-match-report-${stamp}.xlsx`);
};
